import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Heart, Award, Sprout, TrendingUp, Shield, Users, Building } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export const About = () => {
  const { language } = useLanguage();
  const t = translations.about;

  const values = [
    {
      icon: Target,
      title: t.values.items[0].title[language],
      description: t.values.items[0].description[language],
      color: '#FF6600'
    },
    {
      icon: Building,
      title: t.values.items[1].title[language],
      description: t.values.items[1].description[language],
      color: '#003366'
    },
    {
      icon: Award,
      title: t.values.items[2].title[language],
      description: t.values.items[2].description[language],
      color: '#FFB81C'
    },
    {
      icon: Sprout,
      title: t.values.items[3].title[language],
      description: t.values.items[3].description[language],
      color: '#00A651'
    },
    {
      icon: TrendingUp,
      title: t.values.items[4].title[language],
      description: t.values.items[4].description[language],
      color: '#FF6600'
    },
  ];

  const governance = [
    {
      icon: Shield,
      title: t.governance.items[0].title[language],
      description: t.governance.items[0].description[language]
    },
    {
      icon: TrendingUp,
      title: t.governance.items[1].title[language],
      description: t.governance.items[1].description[language]
    },
    {
      icon: Users,
      title: t.governance.items[2].title[language],
      description: t.governance.items[2].description[language]
    },
    {
      icon: Building,
      title: t.governance.items[3].title[language],
      description: t.governance.items[3].description[language]
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#003366] via-[#004080] to-[#003366]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t.hero.title[language]}
            </h1>
            <p className="text-2xl text-[#FFB81C] font-semibold mb-8">
              {t.hero.subtitle[language]}
            </p>
            <p className="text-xl text-gray-200 leading-relaxed">
              {t.hero.description[language]}
            </p>
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#003366] mb-6">
                {t.presentation.title[language]}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {t.presentation.text1[language]}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.presentation.text2[language]}
              </p>
            </div>

            <Card className="border-2 border-[#FF6600] shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-[#FF6600] p-3 rounded-lg flex-shrink-0">
                    <Heart className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#003366] pt-2">{t.presentation.missionTitle[language]}</h3>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t.presentation.missionText[language]}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              {t.values.title[language]}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.values.subtitle[language]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-shadow duration-300 border-0 shadow-md">
                  <CardContent className="p-8">
                    <div 
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                      style={{ backgroundColor: `${value.color}15` }}
                    >
                      <Icon style={{ color: value.color }} size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-[#003366] mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
              {t.governance.title[language]}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.governance.subtitle[language]}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {governance.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-[#FF6600] transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#003366] p-3 rounded-lg flex-shrink-0">
                        <Icon className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#003366] mb-2">{item.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#00A651] to-[#008a43]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t.cta.title[language]}
          </h2>
          <p className="text-xl text-white/90 mb-10">
            {t.cta.description[language]}
          </p>
        </div>
      </section>
    </div>
  );
};